import { Button } from '@vrplatform/components';
import { Box, Group, LoadingOverlay, Text, Title } from '@mantine/core';
import { Modal } from '@mantine/core';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import type { AdvancedDepositsModalFormInputs } from './_types';
import { useAdvancedDepositsModalQuery } from './useAdvancedDepositsModalQuery';
import { AdvancedDepositsModalTable } from './AdvancedDepositsModalTable';
import { SelectReservationModal } from '../../components';
import { useDisclosure } from '@mantine/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useAdvancedDepositsModalMutation } from './useAdvancedDepositsModalMutation';
import { showApiErrorNotification, useApiClient } from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import { CsvImportModal } from '../csv-import-modal/CsvImportModal';
import { useImportCsvConnectionQuery } from '@vrplatform/data';

const emptyFormValues: AdvancedDepositsModalFormInputs = {
  accountId: undefined,
  reservations: [],
};

export const AdvancedDepositsModal = ({
  opened,
  closeModal,
  refetch,
}: {
  opened: boolean;
  closeModal: () => void;
  refetch: () => void;
}) => {
  'use memo';

  const [select, handlers] = useDisclosure(false);
  const [csvImportOpened, setCsvImportOpened] = useState(false);

  const queryData = useAdvancedDepositsModalQuery();
  const importCsvConnectionQuery = useImportCsvConnectionQuery();

  const methods = useForm<AdvancedDepositsModalFormInputs>({
    values: queryData.data,
  });

  const api = useApiClient();

  const fieldArray = useFieldArray({
    name: 'reservations',
    control: methods.control,
  });

  const { submit } = useAdvancedDepositsModalMutation({
    queryDataKey: queryData.queryKey,
    closeModal,
    refetch,
  });

  const advancedDepositsImporter = useMemo(
    () =>
      importCsvConnectionQuery.data?.importers.find(
        (importer) => importer.uniqueRef === 'advancedDeposits'
      ),
    [importCsvConnectionQuery.data?.importers]
  );

  const csvImportConnection = useMemo(() => {
    if (!importCsvConnectionQuery.data || !advancedDepositsImporter) {
      return undefined;
    }

    return {
      id: importCsvConnectionQuery.data.id,
      app: {
        ...importCsvConnectionQuery.data.app,
        documentationUrl: advancedDepositsImporter.docsLink,
      },
    };
  }, [advancedDepositsImporter, importCsvConnectionQuery.data]);

  const resetModal = useCallback(() => {
    setCsvImportOpened(false);
    closeModal();
    methods.reset(queryData.data ?? emptyFormValues);
  }, [closeModal, methods, queryData.data]);

  const openCsvImportModal = useCallback(() => {
    handlers.close();

    if (!importCsvConnectionQuery.data) {
      showWarnNotification({
        title: 'CSV import unavailable',
        message:
          'We could not find the CSV import connection for this team. Please contact support if this issue persists.',
      });
      return;
    }

    if (!advancedDepositsImporter) {
      showWarnNotification({
        title: 'Advance deposits CSV import unavailable',
        message:
          'The advance deposits CSV importer is not configured for this team yet.',
      });
      return;
    }

    setCsvImportOpened(true);
  }, [advancedDepositsImporter, handlers, importCsvConnectionQuery.data]);

  const closeCsvImportModal = useCallback(() => {
    setCsvImportOpened(false);
  }, []);

  const handleCsvImportCompleted = useCallback(async () => {
    const refreshed = await queryData.refetch();
    methods.reset(refreshed.data ?? emptyFormValues);
    await Promise.resolve(refetch());
  }, [methods, queryData, refetch]);

  const reservationIds =
    useWatch({
      control: methods.control,
      name: 'reservations',
    })?.map((r) => r.reservationId) ?? [];

  return (
    <>
      <SelectReservationModal
        activeReservationId={null}
        closeModal={handlers.close}
        withOverlay={false}
        opened={select}
        handleReservationIdSelect={async (reservationId) => {
          const reservations = methods.getValues('reservations');

          if (reservations.find((r) => r.reservationId === reservationId))
            return;

          const reservation = await api
            .GET(
              '/teams/opening-balances/advanced-deposits/synced-payments-refunds',
              {
                params: {
                  query: {
                    reservationsIds: reservationId,
                  },
                },
              }
            )
            .then((result) => {
              if (result.error) {
                showApiErrorNotification({
                  error: result.error,
                  title: 'Failed to load synced payments/refunds',
                });
                throw result.error;
              }

              return result.data.reservations[0];
            });

          if (!reservation) return;

          fieldArray.prepend({
            reservationId,
            paymentCentTotal: 0,
            refundCentTotal: 0,
            synced: {
              paymentCentTotal: reservation.payment,
              refundCentTotal: reservation.refund,
              transactionIds: reservation.transactions.map(
                (t) => t.transactionId
              ),
            },
            checkIn: reservation.checkIn,
            checkOut: reservation.checkOut,
            guestName: reservation.guestName,
            confirmationCode: reservation.confirmationCode,
            reservationCentTotal: reservation.reservationAmount ?? 0,
          });

          handlers.close();
        }}
        title="Select reservation"
        subtitle="Select a reservation to add manual payments or refunds."
        submitLabel="Add reservation"
        excludedReservationIds={reservationIds}
      />

      <CsvImportModal
        opened={csvImportOpened}
        closeModal={closeCsvImportModal}
        connection={csvImportConnection}
        uniqueRef="advancedDeposits"
        onSuccess={handleCsvImportCompleted}
      />

      <Modal
        opened={opened && !csvImportOpened}
        onClose={resetModal}
        centered
        size={1300}
        zIndex={120}
        title={
          <Box>
            <Title order={4} mb="xs">
              Review Advance Deposits
            </Title>
            <Text span c="neutral" size="sm" ta="left" display={'block'}>
              We've imported all advance payments collected for upcoming
              reservations and matched them with your PMS records. Confirm or
              add any manual deposits to ensure accurate opening balances. This
              step is required only once. You can make later adjustments in the
              settings.
            </Text>
          </Box>
        }
        styles={{
          body: {
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            margin: 0,
            gap: 0,
            minHeight: '75dvh',
            position: 'relative',
          },
          header: {
            alignItems: 'flex-start',
          },
        }}
      >
        <LoadingOverlay visible={queryData.isFetching} />
        <FormProvider {...methods}>
          <AdvancedDepositsModalTable
            csvImportLoading={importCsvConnectionQuery.isLoading}
            openCsvImportModal={openCsvImportModal}
            openSelectReservationModal={handlers.open}
            {...fieldArray}
          />

          <Group
            pt="xs"
            pb="xs"
            px="sm"
            justify="flex-end"
            sx={(theme) => ({
              borderTop: `1px solid ${theme.colors.gray[2]}`,
            })}
          >
            <Button
              type="reset"
              onClick={resetModal}
              disabled={methods.formState.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={methods.handleSubmit(submit)}
              loading={methods.formState.isSubmitting}
              variant="primary"
            >
              Submit
            </Button>
          </Group>
        </FormProvider>
      </Modal>
    </>
  );
};
